import { z } from "zod";

const marketplaceTabSchema = z.enum([
  "vendors",
  "in-production",
  "history",
  "archive",
]);

const vendorStatusSchema = z.enum(["connected", "available"]);
const vendorSortSchema = z.enum(["recommended", "fastest", "cheapest", "recent"]);
const turnaroundSchema = z.enum(["fast", "standard", "longer"]);
const priceSchema = z.enum(["budget", "mid", "premium"]);

const marketplaceSearchSchema = z.preprocess((raw) => {
  if (typeof raw !== "object" || raw === null) return raw;
  const search = { ...(raw as Record<string, unknown>) };
  if (search.tab === "connections") {
    search.tab = "vendors";
    if (search.status == null) search.status = "connected";
  }
  if (search.tab === "catalog") search.tab = "vendors";
  if (search.tab === "orders" || search.tab === "pending-orders") {
    search.tab = "in-production";
  }
  return search;
}, z.object({
  tab: marketplaceTabSchema.optional().catch(undefined),
  /** Order id to open + highlight when landing on an orders tab (deep link). */
  order: z.string().optional().catch(undefined),
  /** Script line id to reopen the work panel (`?script=`). */
  script: z.string().optional().catch(undefined),
  /** Vendor detail drawer (`?vendor=`). */
  vendor: z.string().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  status: vendorStatusSchema.optional().catch(undefined),
  capability: z.string().optional().catch(undefined),
  turnaround: turnaroundSchema.optional().catch(undefined),
  price: priceSchema.optional().catch(undefined),
  sort: vendorSortSchema.optional().catch(undefined),
}));

export type MarketplaceSearch = z.infer<typeof marketplaceSearchSchema>;
export type MarketplaceTab = NonNullable<MarketplaceSearch["tab"]>;

export function toMarketplaceSearch(raw: unknown): MarketplaceSearch {
  const parsed = marketplaceSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

/**
 * Merge a search patch into the current marketplace URL.
 * `undefined` on an explicit key (e.g. `{ vendor: undefined }`) clears that param.
 * Omitting the key keeps the previous value.
 */
export function mergeMarketplaceSearch(
  prev: MarketplaceSearch,
  patch: Partial<MarketplaceSearch>,
): MarketplaceSearch {
  const nextTab = patch.tab ?? prev.tab ?? "vendors";
  const onOrders =
    nextTab === "in-production" ||
    nextTab === "history" ||
    nextTab === "archive";
  return {
    ...prev,
    ...patch,
    tab: nextTab,
    order: onOrders ? ("order" in patch ? patch.order : prev.order) : undefined,
    script: onOrders ? ("script" in patch ? patch.script : prev.script) : undefined,
    vendor:
      nextTab === "vendors"
        ? "vendor" in patch
          ? patch.vendor
          : prev.vendor
        : undefined,
  };
}
